import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BlankSpace, ContainerScroll } from '../genericComponents/layouts';
import {
  CardTitle,
  Text3, Text4,
  Text4B,
} from '../genericComponents/texts'; import feliz from '../assets/img/feliz.png';
import mediofeliz from '../assets/img/mediofeliz.png';
import nofeliz from '../assets/img/nofeliz.png';
import molesto from '../assets/img/molesto.png';
import furioso from '../assets/img/furioso.png';

import CardWarning from '../genericComponents/CardWarning';
import { priceFormat, roundPrice } from '../priceFormat';
import ConditionPrices from '../models/conditionPrices.model';
import ResidualPrices from '../models/residualPrices.model';
import KmSimulation from '../models/KmSimulation.model';

interface Props{
  vehicleCompleteName: string;
  conditionPrices: ConditionPrices;
  residualPrices?: ResidualPrices[];
  kmSimulation?: KmSimulation | undefined;
  autofactPrice: number;
}

export default function ConditionalPriceAdjustmen({
  vehicleCompleteName,
  conditionPrices,
  residualPrices,
  kmSimulation,
  autofactPrice,
}: Props) {
  const Mensajes = {
    vr_value: 'Valor no encontrado',
  };

  const auctionPrice = autofactPrice * 0.6;

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  useEffect(() => {
    const allValues = [
      conditionPrices.bad_value ?? 0,
      conditionPrices.regular_value ?? 0,
      conditionPrices.good_value ?? 0,
      conditionPrices.excelent_value ?? 0,
      auctionPrice,
    ];

    if (allValues.length > 0) {
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      setMinValue(min);
      setMaxValue(max);
    } else {
      setMinValue(0);
      setMaxValue(0);
    }
  }, [conditionPrices]);

  const range = maxValue - minValue;

  const difAutofactPrice = autofactPrice - minValue;
  const percentageAutofactPrice = Math.round((difAutofactPrice * 100) / range);

  // Crear array de tarjetas ordenadas por precio
  const sortedCards = [
    {
      price: auctionPrice,
      text: 'Vehículo con siniestro declarado como "pérdida" por institución oficial de aseguradoras.',
      textIcon: 'Pérdida total reparable',
      icon: furioso,
      type: 'auction',
    },
    {
      price: conditionPrices.bad_value || 0,
      text: 'Tiene desgastes notorios, estéticos en su carrocería y carece de registros de mantenciones por kilometraje.',
      textIcon: 'Regular',
      icon: molesto,
      type: 'condition',
    },
    {
      price: conditionPrices.regular_value || 0,
      text: 'Tiene desgastes acordes a su uso y a su kilometraje, posee registros de mantención automotriz.',
      textIcon: 'Bueno',
      icon: nofeliz,
      type: 'condition',
    },
    {
      price: conditionPrices.good_value || 0,
      text: 'Vehículo en buen estado con registros de mantención automotriz.',
      textIcon: 'Muy bueno',
      icon: mediofeliz,
      type: 'condition',
    },
    {
      price: conditionPrices.excelent_value || 0,
      text: 'Excelente estado de carrocería con todas sus piezas originales y con registro completo de mantención automotriz.',
      textIcon: 'Excelente',
      icon: feliz,
      type: 'condition',
    },
  ].sort((a, b) => a.price - b.price);

  const renderBoxes = () => {
    const boxes = [];
    let i = 0;
    while (i <= 100) {
      boxes.push(
        <Box key={i} />,
      );
      i += 1;
    }
    return boxes;
  };

  return (
    <Container>
      <SubCont1>
        <SubCont3 style={{
          position: 'relative', width: '90%', margin: 'auto', height: '60px',
        }}
        >
          {renderBoxes()}
          <CardPriceAutofact
            price={`$${priceFormat(roundPrice(autofactPrice, 5))}`}
            style={{
              position: 'absolute',
              left: `${percentageAutofactPrice}%`,
              top: 0,
              zIndex: 2,
              width: '120px',
              minWidth: '100px',
              pointerEvents: 'none',
              transform: 'translateX(-50%)',
            }}
          />
        </SubCont3>
        <ContainerScroll>
          {sortedCards.map((card, index) => (
            <CardItemComparative
              // eslint-disable-next-line react/no-array-index-key
              key={`${card.type}-${card.textIcon}-${index}`}
              price={`${priceFormat(roundPrice(card.price, 5))}`}
              text={card.text}
              textIcon={card.textIcon}
              icon={card.icon}
            />
          ))}
        </ContainerScroll>
        <CardWarning
          text="Este tipo de precio según condición del vehículo se calculó en torno al precio de mercado Autofact"
        />

      </SubCont1>

      <BlankSpace height="20px" />

      <SubCont1>
        <ContainerScroll>
          <CardList>
            <CardTitle>
              <Text4B>
                ¿Cuánto costará
                {' '}
                {vehicleCompleteName}
                {' '}
                en el futuro?
              </Text4B>
            </CardTitle>
            <BlankSpace height="10px" />
            { !residualPrices ? (
              <CardWarning text="No se cuenta con información de precios debido a que el auto tiene más de 15 años de antigüedad." />
            ) : (
              <Table>
                <tbody>
                  <tr style={{ background: '#F6F6F6' }}>
                    <Tableth>Año</Tableth>
                    <Tableth>Depreciación respecto a 0 km</Tableth>
                    <Tableth>Precio estimado</Tableth>
                  </tr>
                  {residualPrices?.map(({ year, vr_value: vrValue, currency_value: currencyValue }, idx) => {
                    const rowBg = idx % 2 === 0 ? '#fff' : '#F6F6F6';
                    return (
                      <tr key={`${year} ${vrValue}`} style={{ background: rowBg }}>
                        <Tabletd><Text4>{year}</Text4></Tabletd>
                        <Tabletd>
                          <Text4>
                            {`${vrValue ? (vrValue * 100).toFixed(1) : Mensajes.vr_value}`}
                            %
                          </Text4>
                        </Tabletd>
                        <Tabletd>
                          $
                          {currencyValue ? priceFormat(roundPrice(currencyValue, 5)) : Mensajes.vr_value}
                        </Tabletd>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </CardList>
          <BlankSpace width="10px" height="20px" />
          <CardList>
            <CardTitle>
              <Text4B>
                ¿Cuánto cuesta
                {' '}
                {vehicleCompleteName}
                {' '}
                según diferencias de kilometraje?
              </Text4B>
            </CardTitle>
            <BlankSpace height="10px" />
            { !kmSimulation?.simulated_kms ? (
              <CardWarning text="No se cuenta con información para vehículos año en curso." />
            ) : (
              <Table>
                <tbody>
                  <tr style={{ background: '#F6F6F6' }}>
                    <TablethLeft>Kilometraje</TablethLeft>
                    <TablethRight>Precio estimado</TablethRight>
                  </tr>
                  {kmSimulation && kmSimulation.simulated_kms && Object.keys(kmSimulation.simulated_kms).map((key, idx) => {
                    const rowBg = idx % 2 === 0 ? '#fff' : '#F6F6F6';
                    // BY_REFERENCE_KM | BY_INPUT_KM
                    const methodUsed = kmSimulation.method_used;
                    const kmSelected = methodUsed === 'BY_REFERENCE_KM'
                      ? Math.round((kmSimulation.reference_km ?? 0) / 100) * 100
                      : Math.round((kmSimulation.input_km ?? 0) / 100) * 100;
                    const textMethodUsed = methodUsed === 'BY_REFERENCE_KM' ? '(Km de referencia)' : '(Km ingresado)';
                    return (
                      <tr key={key} style={{ background: rowBg }}>
                        <Td>
                          {kmSelected === kmSimulation.simulated_kms[key] ? (
                            <Text4B>
                              {priceFormat(kmSimulation.simulated_kms[key])}
                              {' '}
                              {textMethodUsed}
                            </Text4B>
                          ) : (
                            <Text4>
                              {priceFormat(kmSimulation.simulated_kms[key])}
                            </Text4>
                          )}
                        </Td>
                        <TabletdRight>
                          $
                          {kmSimulation.simulated_prices ? priceFormat(roundPrice(kmSimulation.simulated_prices[key], 5)) : '-'}
                        </TabletdRight>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </CardList>
        </ContainerScroll>
      </SubCont1>

    </Container>
  );
}

function CardItemComparative({
  price, text, textIcon, icon,
}: { price: string, text: string, textIcon: string, icon: string }) {
  return (
    <CardItemComparativeContainer>
      <ItemComparativeSubCont1>
        <ItemPrice>
          $
          {price}
        </ItemPrice>
        <TriangleBottom />
      </ItemComparativeSubCont1>

      <ItemComparativeSubCont2>
        <img src={icon} width={20} height={20} alt="" />
        <BlankSpace width="5px" />
        <Text4>{textIcon}</Text4>
      </ItemComparativeSubCont2>

      <ItemComparativeSubCont3>
        <Text3>{text}</Text3>
      </ItemComparativeSubCont3>
    </CardItemComparativeContainer>
  );
}

function CardPriceAutofact({ price, style }: { price: string | null, style?: React.CSSProperties }) {
  return (
    <CardItemComparativeContainer style={style}>
      {price ? (
        <>
          <ItemPrice style={{
            backgroundColor: '#0b3254',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px 0 5px 0',
          }}
          >
            <Text4 style={{ textAlign: 'center', color: '#fff', marginBottom: '2px' }}>Precio Autofact</Text4>
            <span style={{ fontWeight: 700, fontSize: '16px', color: '#fff' }}>{price}</span>
          </ItemPrice>
          <TriangleBottomPriceAf style={{
            position: 'absolute',
            left: '50%',
            top: '76%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
          />
        </>
      ) : ''}
    </CardItemComparativeContainer>
  );
}

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  justify-items: flex-start;
  width: 95%;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  margin: 0 5px;
  @media (max-width: 700px){
    width: 90%;
  }
`;

const Container = styled.div`
  width: 100%;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  @media (max-width: 700px){
    flex-direction: column;
  }
`;
const SubCont1 = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  justify-items: flex-start;
  width: 100%;
  @media (max-width: 700px){
    width: unset;
    flex:1;
  }
`;

const CardItemComparativeContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 20px 10px;
  margin: 0px 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (max-width: 700px){
    width: 90%;
    left: 50% !important;
  }
`;
const ItemComparativeSubCont1 = styled.div`
  display: flex;
  flex-direction: column;
  height: 'auto';
  align-items: center;
  
`;
const ItemComparativeSubCont2 = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 10px 0px;
  width: 120px;
  height: 20px;
`;

const ItemComparativeSubCont3 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
`;
const ItemPrice = styled.label`
  width: 120px;
  min-width: 100px;
  background-color: #155892;
  border-radius: 15px;
  color: #fff;
  text-align: center;
  align-items: center;
  padding: 5px;
`;
const TriangleBottom = styled.div`
  width: 0;  
  height: 0;
  margin: 0 auto;
  border-right: 10px solid transparent;
  border-top: 10px solid #155892; 
  border-left: 10px solid transparent;
  border-bottom: 10px solid transparent;
`;
const TriangleBottomPriceAf = styled.div`
  width: 0;  
  height: 0;
  margin: 0 auto;
  border-right: 10px solid transparent;
  border-top: 10px solid #0b3254; 
  border-left: 10px solid transparent;
  border-bottom: 10px solid transparent;
  @media (max-width: 700px){
    width: 100%;
    border-top: 10px solid transparent; 
  }
`;
const Table = styled.table`
  margin: 0px 0;
  border: none;
  width: 100%;
  @media (max-width: 700px){
    width: 100%;
  }
`;

const Tableth = styled.th`
  font-size: 14px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: center;
  padding: 15px 0;
`;

const TablethLeft = styled.th`
  font-size: 14px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: left;
  padding: 15px 0;
`;

const TablethRight = styled.th`
  font-size: 14px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: right;
  padding: 15px 0;
`;

const Td = styled.td`
  padding: 10px;
`;

const Tabletd = styled.td`
  font-size: 14px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: center;
  padding: 10px;
  @media (max-width: 700px){
    width: 10%;
    padding: 15px 0;
  }
`;

const TabletdRight = styled.td`
  font-size: 14px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: right;
  padding: 10px 0;
  @media (max-width: 700px){
    width: 10%;
    padding: 15px 0;
  }
`;
const SubCont3 = styled.div`
  width: 95%;
  display: flex;
  flex-direction: row;
  margin: auto;
  @media (max-width: 700px){
    width: 100%;
  }
`;

const Box = styled.div`
  width: 1%;
  height: 40px;
  background: #fff;
  display: inline-block;
`;
